import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useContext } from "react";
import { Context } from "@/ui-components/utils/root/context";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui-components/ui/card";
import { Button } from "@/ui-components/ui/button";

const DebugAuth = () => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { accessToken } = useContext(Context);
  const [tokenDetails, setTokenDetails] = useState<any>(null);
  const [apiTestResult, setApiTestResult] = useState<string>("");

  const decodeJWT = (token: string) => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      const payload = JSON.parse(atob(parts[1]));
      return payload;
    } catch (e) {
      return null;
    }
  };

  const fetchTokenDetails = async () => {
    try {
      // Try to get token with audience
      const tokenWithAudience = await getAccessTokenSilently({
        authorizationParams: {
          scope: "openid profile email offline_access",
          audience: process.env.REACT_APP_AUTH0_AUDIENCE
        },
      });

      // Try to get token without audience (to see the difference)
      const tokenWithoutAudience = await getAccessTokenSilently({
        authorizationParams: {
          scope: "openid profile email offline_access"
        },
      });

      setTokenDetails({
        withAudience: {
          token: tokenWithAudience,
          decoded: decodeJWT(tokenWithAudience)
        },
        withoutAudience: {
          token: tokenWithoutAudience,
          decoded: decodeJWT(tokenWithoutAudience)
        },
        contextToken: {
          token: accessToken,
          decoded: accessToken ? decodeJWT(accessToken) : null
        }
      });
    } catch (error) {
      console.error("Error fetching token:", error);
      setTokenDetails({ error: error instanceof Error ? error.message : String(error) });
    }
  };

  const testAPI = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/v1.0/pipelines/search?limit=1&page=0`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      setApiTestResult(JSON.stringify({
        status: response.status,
        statusText: response.statusText,
        data: data
      }, null, 2));
    } catch (error) {
      setApiTestResult(`Error: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchTokenDetails();
    }
  }, [isAuthenticated]);

  return (
    <div className="p-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Auth0 Debug Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold">User Info:</h3>
            <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto">
              {JSON.stringify(user, null, 2)}
            </pre>
          </div>

          <div>
            <h3 className="font-semibold">Environment Variables:</h3>
            <pre className="bg-gray-100 p-2 rounded text-xs">
              REACT_APP_AUTH0_AUDIENCE: {process.env.REACT_APP_AUTH0_AUDIENCE}
              REACT_APP_API: {process.env.REACT_APP_API}
              REACT_APP_AUTH0_DOMAIN: {process.env.REACT_APP_AUTH0_DOMAIN}
            </pre>
          </div>

          {tokenDetails && (
            <div>
              <h3 className="font-semibold">Token Analysis:</h3>
              
              <div className="mt-2">
                <h4 className="text-sm font-medium">Context Token (used by app):</h4>
                <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto">
                  {tokenDetails.contextToken?.decoded ? JSON.stringify(tokenDetails.contextToken.decoded, null, 2) : 'No token in context'}
                </pre>
              </div>

              <div className="mt-2">
                <h4 className="text-sm font-medium">Token WITH audience:</h4>
                <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto">
                  {tokenDetails.withAudience?.decoded ? JSON.stringify(tokenDetails.withAudience.decoded, null, 2) : 'Failed to decode'}
                </pre>
              </div>

              <div className="mt-2">
                <h4 className="text-sm font-medium">Token WITHOUT audience:</h4>
                <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto">
                  {tokenDetails.withoutAudience?.decoded ? JSON.stringify(tokenDetails.withoutAudience.decoded, null, 2) : 'Failed to decode'}
                </pre>
              </div>
            </div>
          )}

          <div>
            <Button onClick={testAPI}>Test API Call</Button>
            {apiTestResult && (
              <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto mt-2">
                {apiTestResult}
              </pre>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DebugAuth;
